/**
 * Extrai uma mensagem de erro segura para exibir em toast/UI,
 * lidando com formatos do FastAPI/Pydantic:
 *  - string (caso simples): retorna como esta
 *  - lista de ValidationError do Pydantic v2:
 *      [{type, loc, msg, input, ctx}, ...]
 *      -> "Campo1: msg traduzida; Campo2: msg traduzida"
 *  - objeto unico: tenta .msg | .detail | JSON.stringify
 *  - fallback: o `fallback` informado
 *
 * Nunca retorna um objeto - garante que React/sonner consiga renderizar.
 */

// Rotulos amigaveis em PT-BR para nomes tecnicos de campos
const FIELD_LABELS = {
  // Genericos
  nome: "Nome",
  nome_fantasia: "Nome Fantasia",
  razao_social: "Razão Social",
  documento: "Documento",
  cpf: "CPF",
  cnpj: "CNPJ",
  email: "E-mail",
  telefone: "Telefone",
  telefone_whatsapp_1: "Telefone/WhatsApp",
  endereco: "Endereço",
  numero: "Número",
  bairro: "Bairro",
  cidade: "Cidade",
  estado: "Estado",
  cep: "CEP",
  password: "Senha",
  senha: "Senha",
  // Comercial / Financeiro
  cliente_id: "Cliente",
  cliente_nome: "Cliente",
  contrato_id: "Contrato",
  orcamento_id: "Orçamento",
  fornecedor_id: "Fornecedor",
  valor: "Valor",
  valor_total: "Valor Total",
  valor_sinal: "Sinal/Entrada",
  parcelas: "Parcelas",
  data: "Data",
  data_inicio: "Data de Início",
  data_fim: "Data de Término",
  data_vencimento: "Data de Vencimento",
  data_pagamento: "Data de Pagamento",
  data_validade: "Validade",
  data_despesa: "Data da Despesa",
  data_inicio_servico: "Data de Início do Serviço",
  prazo_execucao: "Prazo de Execução",
  forma_pagamento: "Forma de Pagamento",
  descricao: "Descrição",
  observacoes: "Observações",
  conta_id: "Conta Contábil",
  centro_custo_id: "Centro de Custo",
  categoria_id: "Categoria",
  tipo: "Tipo",
  tipo_cadastro: "Tipo de Cadastro",
  tipo_pessoa: "Tipo de Pessoa",
  status: "Status",
  // Cartoes
  card_id: "Cartão",
  apelido: "Apelido",
  bandeira: "Bandeira",
  banco: "Banco",
  dia_vencimento: "Dia de Vencimento",
  melhor_dia_compra: "Melhor Dia para Compra",
  limite: "Limite",
  ultimos_4_digitos: "Últimos 4 dígitos",
  estabelecimento: "Estabelecimento",
  // OS / Servico
  titulo: "Título",
  prioridade: "Prioridade",
  tecnico_id: "Técnico",
  // CRM
  origem: "Origem",
  etapa: "Etapa",
  responsavel_id: "Responsável",
};

// Mensagens tecnicas do Pydantic -> PT-BR (chave eh o `type` retornado)
const PYDANTIC_MSG_PT = {
  missing: "é obrigatório",
  string_too_short: "muito curto",
  string_too_long: "muito longo",
  value_error: "valor inválido",
  type_error: "tipo inválido",
  int_parsing: "deve ser um número inteiro",
  float_parsing: "deve ser um número decimal",
  bool_parsing: "deve ser verdadeiro ou falso",
  datetime_parsing: "data inválida",
  date_parsing: "data inválida",
  email: "e-mail inválido",
  greater_than: "deve ser maior",
  less_than: "deve ser menor",
  greater_than_equal: "deve ser maior ou igual",
  less_than_equal: "deve ser menor ou igual",
};

function fieldLabel(name) {
  if (!name) return "";
  if (FIELD_LABELS[name]) return FIELD_LABELS[name];
  // fallback: snake_case -> Title Case
  return name
    .toString()
    .split("_")
    .map((w) => (w.length === 0 ? w : w[0].toUpperCase() + w.slice(1)))
    .join(" ");
}

function formatPydanticItem(item) {
  if (typeof item === "string") return item;
  if (!item || typeof item !== "object") return null;
  const loc = Array.isArray(item.loc) ? item.loc : [];
  // pega ultimo campo "real" da loc (ignora 'body','query' iniciais)
  const lastField = [...loc].reverse().find((p) => p && p !== "body" && p !== "query" && p !== "path");
  const label = lastField != null ? fieldLabel(lastField) : "";
  let baseMsg = item.msg || item.detail || "valor inválido";
  // se tipo for conhecido, substitui
  const typeMsg = item.type ? PYDANTIC_MSG_PT[item.type] : null;
  if (typeMsg) baseMsg = typeMsg;
  // remove prefixo "Value error, " redundante
  baseMsg = String(baseMsg).replace(/^Value error,\s*/i, "");
  return label ? `${label}: ${baseMsg}` : baseMsg;
}

export function extractErrorMessage(err, fallback = "Erro inesperado") {
  if (!err) return fallback;
  const data = err?.response?.data;
  const candidate = data?.detail ?? data?.message ?? data?.error ?? err?.message ?? err;
  return toMessageString(candidate, fallback);
}

function toMessageString(value, fallback) {
  if (value == null) return fallback;
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value)) {
    const msgs = value.map(formatPydanticItem).filter(Boolean);
    if (msgs.length === 0) return fallback;
    if (msgs.length === 1) return msgs[0];
    // multiplos: junta com " · " que renderiza bonito no sonner
    return msgs.join(" · ");
  }
  if (typeof value === "object") {
    if (typeof value.msg === "string") return value.msg;
    if (typeof value.detail === "string") return value.detail;
    if (typeof value.error === "string") return value.error;
    // tentativa de tratar como pydantic isolado
    const formatted = formatPydanticItem(value);
    if (formatted) return formatted;
    try { return JSON.stringify(value); } catch { return fallback; }
  }
  return fallback;
}
